import { Link, useParams, useLocation } from "@tanstack/react-router";
import type { ComponentType } from "react";
import { Users, Settings, LifeBuoy } from "lucide-react";
import type { ModuleManifest } from "@praesto/contracts";
import { AppIcon } from "@praesto/ui-kit";
import { useCore } from "../lib/core";

/**
 * The 64px icon rail — always Night in both themes, full height (C3F v18).
 * App icon at the top (below 34px the wordmark is illegible, so the symbol
 * carries the mark), one icon per active module + Persone, Guida/Impostazioni
 * anchored at the bottom.
 *
 * Guida uses LifeBuoy, not the manifest's historic BookOpen: on an icon-only
 * rail BookOpen collided with the Procedure module — two entries, same sign,
 * visible together. This is the upstream fix the design system asked for.
 */
export function ModuleSidebar({ manifests }: { manifests: ReadonlyArray<ModuleManifest> }) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const location = useLocation();
  const canManageUsers = core.rbac.can("manage", "user");

  return (
    // surface-night keeps every token-consuming descendant valid on the
    // always-dark rail; overflow-y so the bottom group is scrollable, never
    // silently clipped, on short viewports.
    <aside className="surface-night flex w-16 flex-shrink-0 flex-col items-center gap-1 overflow-y-auto border-r border-border-default bg-sidebar-bg py-2.5 text-sidebar-fg no-scrollbar">
      <AppIcon size={32} className="mb-3 flex-shrink-0" />
      {manifests.map((m) => (
        <IconLink
          key={m.id}
          icon={m.icon as unknown as ComponentType<{ size?: number }>}
          label={core.i18n.t(`${m.id}.title`)}
          to={`/${tenantSlug}/${m.id}`}
          active={location.pathname.startsWith(`/${tenantSlug}/${m.id}`)}
        />
      ))}
      {canManageUsers && (
        <IconLink
          icon={Users as unknown as ComponentType<{ size?: number }>}
          label="Persone"
          to={`/${tenantSlug}/people`}
          active={location.pathname.startsWith(`/${tenantSlug}/people`)}
        />
      )}
      <div className="mt-auto flex flex-col items-center gap-1">
        <IconLink
          icon={LifeBuoy as unknown as ComponentType<{ size?: number }>}
          label={core.i18n.t("help.title")}
          to={`/${tenantSlug}/help`}
          active={location.pathname.startsWith(`/${tenantSlug}/help`)}
        />
        <IconLink
          icon={Settings as unknown as ComponentType<{ size?: number }>}
          label="Impostazioni"
          to={`/${tenantSlug}/settings`}
          active={location.pathname.startsWith(`/${tenantSlug}/settings`)}
        />
      </div>
    </aside>
  );
}

function IconLink({
  icon: Icon,
  label,
  to,
  active,
}: {
  icon: ComponentType<{ size?: number }>;
  label: string;
  to: string;
  active: boolean;
}) {
  return (
    <Link
      to={to as never}
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm transition-colors duration-fast ${
        active
          ? "bg-sidebar-active text-sidebar-fg"
          : "text-sidebar-muted hover:bg-hover hover:text-sidebar-fg"
      }`}
    >
      <Icon size={20} />
    </Link>
  );
}
